import React from 'react';
import { StyleSheet, View, Platform, type StyleProp, type ViewStyle } from 'react-native';

/**
 * Card 组件属性
 */
export interface CardProps {
  /**
   * 子元素
   */
  children: React.ReactNode;
  /**
   * 自定义样式
   */
  style?: StyleProp<ViewStyle>;
  /**
   * 卡片变体
   * - default: 带阴影的默认卡片
   * - flat: 扁平无阴影卡片
   */
  variant?: 'default' | 'flat';
}

/**
 * Card 组件
 * 
 * 通用卡片容器组件，支持默认和扁平两种样式
 */
const shadowKeysToStrip = [
  'shadowColor',
  'shadowOffset',
  'shadowOpacity',
  'shadowRadius',
  'textShadow',
  'textShadowColor',
  'textShadowOffset',
  'textShadowRadius',
];

function stripShadowProps(styleObj?: ViewStyle | null): ViewStyle | undefined {
  if (!styleObj) return undefined;
  const flat = StyleSheet.flatten(styleObj) as ViewStyle;
  if (!flat) return undefined;
  const copy: Record<string, any> = { ...flat };
  for (const k of shadowKeysToStrip) {
    if (k in copy) delete copy[k];
  }
  return copy as ViewStyle;
}

const Card: React.FC<CardProps> = ({ children, style, variant = 'default' }) => {
  // On web, react-native-web warns when style objects contain shadow* or textShadow* props.
  // To avoid runtime warnings caused by callers passing legacy shadow props, strip them here
  // while keeping native (iOS/Android) behavior unchanged.
  const incomingSafeStyle = Platform.OS === 'web' ? stripShadowProps(style as any) : style;

  return (
    <View
      style={[
        styles.card,
        variant === 'flat' && styles.flatCard,
        incomingSafeStyle,
      ]}
    >
      {children}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    ...Platform.select({
      web: { elevation: 3 as any, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
      default: { shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 8, elevation: 3 },
    }),
  },
  flatCard: {
    ...Platform.select({
      web: { elevation: 0 as any },
      default: { shadowOpacity: 0, elevation: 0 },
    }),
  },
});

export default Card;
